import templateUrl from './google-location.html';
import head from 'lodash/head';
import clone from 'lodash/clone';
import reduce from 'lodash/reduce';
import isEmpty from 'lodash/isEmpty';

export const googleLocationComponent = {
    bindings: {
        type: '@',
        getDirectionsDetails: '&',
        originCoverageError: '@',
        destinationCoverageError: '@',
        isMobileView: '@',
        exchangeIconPath: '@',
        getRequestPromise: '&',
        directionDetails: '<'
    },

    templateUrl,

    controller: class GoogleLocationComponent {
        constructor($timeout, $parse, GoogleLocationService) {
            'ngInject';

            this.$timeout = $timeout;
            this.$parse = $parse;
            this.googleLocationService = GoogleLocationService;
        }

        $onInit() {
            this.details = {};
            this.place = {};
            this.originAddress = '';
            this.destinationAddress = '';
            this._isMobileView = this.$parse(this.isMobileView || "false")(false);
            this.isOriginAddressInvalid = false;
            this.isDestinationAddressInvalid = false;
            this.fetchingOriginAddress = false;
            this.fetchingDestinationAddress = false;

            if (this.type === 'direction') {

                this.details.origin = {};
                this.details.destination = {};

                const originId = 'origin';
                const destinationId = 'destination';

                let autoCompleteOriginLocation;
                let autoCompleteDestinationLocation;

                this.$timeout(() => {
                    let originInput = document.getElementById(originId);
                    let destinationInput = document.getElementById(destinationId);

                    autoCompleteOriginLocation = new google.maps.places.Autocomplete(originInput, { types: ['geocode'], componentRestrictions: { country: "us" } });
                    autoCompleteOriginLocation.addListener('place_changed', getOriginAddress.bind(this));

                    autoCompleteDestinationLocation = new google.maps.places.Autocomplete(destinationInput, { types: ['geocode'], componentRestrictions: { country: "us" } });
                    autoCompleteDestinationLocation.addListener('place_changed', getDestinationAddress.bind(this));
                });

                function getOriginAddress() {
                    this.place = autoCompleteOriginLocation.getPlace();
                    this._setOriginAddressDetails(this.place);
                }

                function getDestinationAddress() {
                    this.place = autoCompleteDestinationLocation.getPlace();
                    this._setDestinationAddressDetails(this.place);
                }
            }

            if (this.directionDetails && !isEmpty(this.directionDetails.destination) && !isEmpty(this.directionDetails.origin)) {
                this._setOriginAndDestinationAddress();

                this.setLocationDetailsByPlaceName(this.directionDetails.origin.details.name, "origin")
                    .then(_ => this.setLocationDetailsByPlaceName(this.directionDetails.destination.details.name, "destination"))
                    .then(_ => this._getDirectionDetails());
            }
        }

        swapSourceDestination() {
            if (this.originCoverageError === '' && this.destinationCoverageError === '') {
                // swaping address
                this._swapDirectionName();
                this.details = this._swapDirectionObject(this.details);

                this._getDirectionDetails();
            }
        }

        onChangeOrigin() {
            this.isOriginAddressInvalid = false;
            this.details.origin = this._isPlaceAddressInvalid(this.originAddress, this.details.origin) ? {} : angular.copy(this.details.origin);
            this._getDirectionDetails();
            this.originCoverageError = '';
        }

        onChangeDestination() {
            this.isDestinationAddressInvalid = false;
            this.details.destination = this._isPlaceAddressInvalid(this.destinationAddress, this.details.destination) ? {} : angular.copy(this.details.destination);
            this._getDirectionDetails(false);
            this.destinationCoverageError = '';
        }

        setLocationDetailsByPlaceName(placeName, type) {
            return new Promise((resolve, reject) => {
                let geocoder = new google.maps.Geocoder();
                this.$timeout(() => {
                    return geocoder.geocode({ 'address': placeName }, (results, status) => {
                        if (status === google.maps.GeocoderStatus.OK) {
                            return this._setPlaceDetailsByPlaceId(head(results).place_id, type)
                                .then(_ => resolve());
                        }
                        return {};
                    });
                },500);
            });
        }

        checkOriginAddressValidation() {
            this.$timeout(() => {
                if(this.originAddress && isEmpty(this.details.origin)) {
                    // provided origin address is either invalid
                    // or the user has not clicked from the address suggested
                    // by google in Autocomplete
                    // In such case fetch the address from google for the text input given
                    // by user
                    const originAddressDetailsFetchPromise = this._getPlaceDetailsByText(this.originAddress);
                    originAddressDetailsFetchPromise.then(placeDetails => {
                        if(placeDetails.isAddressInvalid === true) {
                            this.isOriginAddressInvalid = true;
                        } else {
                            // the place is valid
                            // set the details
                            this._setOriginAddressDetails(placeDetails.details);
                        }
                    });
                    this.getRequestPromise({
                        promise: {
                            originAddressDetailsFetchPromise
                        }
                    });
                } else {
                    this.originCoverageError = !this.details.origin.countyName && this.originAddress.length > 0 ? 'Please select service area' : this.originCoverageError;
                }
            }, 500);
        }

        checkDestinationAddressValidation() {
            this.$timeout(_ => {
                if(this.destinationAddress && isEmpty(this.details.destination)) {
                    // provided desitnation address is invalid
                    const destinationAddressDetailsFetchPromise = this._getPlaceDetailsByText(this.destinationAddress);
                    destinationAddressDetailsFetchPromise.then(placeDetails => {
                        if(placeDetails.isAddressInvalid === true) {
                            this.isDestinationAddressInvalid = true;
                        } else {
                            // the place is valid
                            // set the details
                            this._setDestinationAddressDetails(placeDetails.details);
                        }
                    });
                    this.getRequestPromise({
                        promise: {
                            destinationAddressDetailsFetchPromise
                        }
                    });
                }
            }, 500);
            // this.$timeout(() => {
            //     this.destinationCoverageError = this.destinationAddress ? 'Selected area is not serviced for trip planning' : this.destinationCoverageError;
            //     console.log(this.destinationCoverageError);
            // }, 500);
        }

        //********************************************************************************Private Methods *********************************************** */
        _getFullLocationName({ name, formatted_address: formattedAddress }) {
            const formattedName = _removeRepeatedNameFromFormattedAddress();
            return [name.trim(), formattedName].join(',');

            function _removeRepeatedNameFromFormattedAddress() {
                let nameKeywords = name.split(" ");
                nameKeywords = [...nameKeywords, ','];
                
                const address = reduce(nameKeywords, (address, key) => {
                    return address.replace(`${key}`, '');
                }, formattedAddress);

                return address.trim();
            }
        }

        _setPlaceDetailsByPlaceId(placeId, type) {
            return new Promise((resolve, reject) => {
                let service = new google.maps.places.PlacesService(document.getElementById("map_service"));
                return service.getDetails({
                    placeId: placeId
                }, (place, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK) {
                        this._setDirectionDetailsObject(place, type);
                        const placeDetails = this.details[type];
                        this._getCountyDetailsIfNotExistInPlaceDetails(placeDetails)
                            .then(placeDetails => this.details[type] = placeDetails)
                            .then(_ => resolve());
                    }
                });
            });
        }

        _setDirectionDetailsObject(placeDetails, type) {
            if (type === 'origin') {
                // this.originAddress = this.directionDetails.origin.details.name;
                this.details.origin = angular.copy(this.googleLocationService.getLocationObject(placeDetails));
                this.details.origin.name = this.originAddress;
            } else {
                // this.destinationAddress = this.directionDetails.destination.details.name;
                this.details.destination = angular.copy(this.googleLocationService.getLocationObject(placeDetails));
                this.details.destination.name = this.destinationAddress;
            }
        }

        _setOriginAndDestinationAddress() {
            this.$timeout(() => {
                this.originAddress = this.directionDetails.origin.details.name;
                this.destinationAddress = this.directionDetails.destination.details.name;
            });
        }

        _getCountyDetailsIfNotExistInPlaceDetails(placeDetails) {
            return new Promise((resolve, reject) => {
                if (this._isCountyDetailsExistInGivenPlaceDetails(placeDetails)) {
                    resolve(placeDetails);
                } else {
                    this.googleLocationService.getLocationDetailsByLatlng(placeDetails.latlng)
                        .then(([result]) => {
                            const { countyName } = this.googleLocationService.getLocationObject(result);
                            placeDetails.countyName = countyName;
                            resolve(placeDetails);
                        });
                }
            });
        }

        _isPlaceAddressInvalid(address, placeDetails) {
            return !address || address.length === 0 || address !== placeDetails.name;
        }

        _isCountyDetailsExistInGivenPlaceDetails({ countyName = false }) {
            return countyName ? true : false;
        }

        _swapDirectionName() {
            this.originAddress = this.destinationAddress + (this.destinationAddress = this.originAddress, "")
        }

        _swapDirectionObject(details) {
            let { destination: origin, origin: destination } = details;

            return {
                origin,
                destination
            };
        }

        _setOriginAddressDetails(placeDetails) {
            this.originCoverageError = '';
            this.details.origin = this.googleLocationService.getLocationObject(placeDetails);
            this.originAddress = this._getFullLocationName(placeDetails);
            this.details.origin.name = this.originAddress;
            this._getDirectionDetails();
        }

        _setDestinationAddressDetails(placeDetails) {
            this.destinationCoverageError = '';
            this.details.destination = this.googleLocationService.getLocationObject(placeDetails);
            this.destinationAddress = this._getFullLocationName(placeDetails);
            this.details.destination.name = this.destinationAddress;
            this._getDirectionDetails(false);
        }

        _getPlaceDetailsByText(text) {
            // get place details by free text
            return this.googleLocationService.getPlaceDetailsByText(text);
        }

        _getDirectionDetails(shouldValidateOrigin = true) {
            return this.getDirectionsDetails({
                $event: {
                    details: this.details,
                    shouldValidateOrigin
                }
            });
        }

        _getErrorMessage() {
            return 'Please select valid address'
        }
    }
};


// WEBPACK FOOTER //
// ./src/app/common/components/google-location/google-location.component.js